import React from 'react';
import { cn } from '@/hen-ui/utils';
import { Image } from '@/hen-ui/atomics/Image';
import { imageCardVariants } from './image-card.variants';

export type ImageCardFullWidthProps = {
  /** Image source URL */
  imageUrl: string;
  /** Alt text for the image */
  imageAlt: string;
  /** Optional styling for the image */
  imgClass?: string;
  /** Defines the content area under the image and supports any valid React node (text, buttons, etc.) */
  children: React.ReactNode;
  /** Optional configuration props (e.g., className, aria-*) for styling and accessibility */
  className?: string;
};

export const ImageCardFullWidth: React.FC<ImageCardFullWidthProps> = ({
  imageUrl,
  imageAlt,
  imgClass,
  children,
  className,
}) => {
  return (
    <div
      className={cn(imageCardVariants({ variant: 'full-width' }), className)}
      role='article'
      aria-label={imageAlt}
    >
      {/* Full-size background image */}
      <Image
        src={imageUrl}
        alt={imageAlt}
        loading='lazy'
        decoding='async'
        rounded={false}
        objectFit='cover'
        className={cn(
          'absolute inset-0 w-full h-full object-cover z-0',
          imgClass,
        )}
      />
      {/* Bottom-up black gradient overlay for text readability (WCAG compliant) */}
      <div
        className='absolute inset-0 w-full h-full pointer-events-none z-[1] bg-image-card-overlay'
        aria-hidden='true'
      />
      {/* Content positioned at bottom center */}
      {children && (
        <div className='absolute inset-x-0 bottom-0 flex flex-col gap-xs items-center text-center p-article-overlay z-[2]'>
          {children}
        </div>
      )}
    </div>
  );
};
